<script lang="ts">
    import { service } from "@frontend/services";
    import type { ApiResultOfBannersDTO, ApiResultOfCampaignsDTO, ApiResultOfGameServerDTO, AuthenticatedRequestOfEditBannerDTO, AuthenticatedRequestOfEditCampaignsDTO, AuthenticatedRequestOfEditGameServerDTO, BannersDTO, CampaignsDTO, EditBannerDTO, EditCampaignsDTO, EditGameServerDTO, GameServerDTO } from "@frontend/walletapi";
    import { TypePromotion } from "@frontend/walletapi";
    import { onMount } from "svelte";
    import { translate, currentLanguage } from "../Translator";
    
    export let campaign: CampaignsDTO;
    export let dismiss: () => void;
    
    let result: ApiResultOfCampaignsDTO | undefined = undefined;
    
    // Variable para el input de StartAmount (en dólares)
    let startAmountInput: number | string = 0;
    
    // Variables para el tiempo
    let days: number = 0;
    let hours: number = 0;
    let minutes: number = 0;
    let seconds: number = 0;
    

    
    // Variable local para la fecha de expiración
    let expirationDateValue: string = "";

    // Variables para la configuración JSON estructurada
    let showAdvancedSettings: boolean = true;
    let promotions: Array<{
        type: string;
        ranges?: Array<{min: number, max: number | null, displayAmount: number}>;
        fixedAmount?: number;
    }> = [];

    let editcampaigns: EditCampaignsDTO = {
        Id: campaign.Id,
        Type: campaign.Type,
        PromotionName: campaign.PromotionName,
        Description: campaign.Description,
        StartAmount: undefined, // Se maneja por separado
        DepositRequired: campaign.DepositRequired,
        Active: campaign.Active,
        TotalDays: campaign.TotalDays, // This is already a string from the API
        ExpirationDate: campaign.ExpirationDate, // Keep as Date object
        Settings: campaign.Settings || "", // Inicializar con string vacío si es null
        Days: days,
        Hours: hours,
        Minutes: minutes,
        Seconds: seconds
    };

    // Inicializar la fecha de expiración en onMount
    onMount(() => {
        if (campaign.StartAmount && campaign.StartAmount.InCents !== undefined) {
            startAmountInput = campaign.StartAmount.InCents / 100;
        } else {
            startAmountInput = 0;
        }
        
        // Parsear el timespan inicial
        parseTimespan(campaign.TotalDays);
        
        // Inicializar la fecha de expiración
        expirationDateValue = formatDateForInput(campaign.ExpirationDate);

        // Parsear la configuración JSON existente
        parseExistingSettings(campaign.Settings);
    });

    // Función para parsear timespan a días, horas, minutos y segundos
    function parseTimespan(timespan: string) {
        if (!timespan) {
            days = 0; hours = 0; minutes = 0; seconds = 0;
            return;
        }
        
        const parts = timespan.split(':');
        if (parts.length === 3) {
            if (parts[0].includes('.')) {
                // Formato con días: "DD.HH:MM:SS"
                const dayHourParts = parts[0].split('.');
                days = parseInt(dayHourParts[0]) || 0;
                hours = parseInt(dayHourParts[1]) || 0;
            } else {
                // Formato sin días: "HH:MM:SS"
                days = 0;
                hours = parseInt(parts[0]) || 0;
            }
            minutes = parseInt(parts[1]) || 0;
            seconds = parseInt(parts[2]) || 0;
        } else {
            days = 0; hours = 0; minutes = 0; seconds = 0;
        }
    }
    
    // Función para convertir días, horas, minutos y segundos a formato timespan
    function convertToTimespan(): string {
        // Asegurar que todos los valores sean números válidos
        const d = days || 0;
        const h = hours || 0;
        const m = minutes || 0;
        const s = seconds || 0;
        
        if (d === 0 && h === 0 && m === 0 && s === 0) {
            return "0";
        }
        
        if (d > 0) {
            // Formato con días: "DD.HH:MM:SS"
            return `${d}.${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        } else {
            // Formato sin días: "HH:MM:SS"
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
    }
    
    // Helper function to format date for input[type="date"]
    function formatDateForInput(dateString: string | Date | null | undefined): string {
        if (!dateString) return "";
        try {
            const date = dateString instanceof Date ? dateString : new Date(dateString);
            if (isNaN(date.getTime())) return "";
            return date.toISOString().split('T')[0];
        } catch {
            return "";
        }
    }

    // Función para generar el JSON estructurado
    function generateSettingsJSON(): string {
        // Si no hay promociones, devolver un objeto vacío
        if (promotions.length === 0) {
            return "";
        }
        
        // Crear una copia profunda de promotions para modificar los valores
        const processedPromotions = promotions.map(promotion => ({
            ...promotion,
            ranges: promotion.ranges ? promotion.ranges.map(range => ({
                ...range,
                max: range.max === 0 ? null : range.max // Convertir 0 de vuelta a null para el JSON
            })) : undefined
        }));
        
        const settings: any = {
            promotions: processedPromotions
        };

        return JSON.stringify(settings, null, 2);
    }

    // Función para parsear el JSON existente
    function parseExistingSettings(settingsString: string | undefined) {
        if (!settingsString) {
            // Si no hay settings, dejar el array vacío para que el usuario pueda agregar configuración
            promotions = [];
            return;
        }
        
        try {
            const settings = JSON.parse(settingsString);
            if (settings.promotions && Array.isArray(settings.promotions)) {
                promotions = settings.promotions.map((promotion: any) => ({
                    type: promotion.type || "Recharge",
                    ranges: promotion.ranges ? promotion.ranges.map((range: any) => ({
                        min: range.min || 0,
                        max: range.max === null ? 0 : range.max, // Convertir null a 0 para el input
                        displayAmount: range.displayAmount || 0
                    })) : undefined,
                    fixedAmount: promotion.fixedAmount
                }));
            }
        } catch (e) {
            console.error("Error parsing settings:", e);
            // En caso de error, dejar el array vacío
            promotions = [];
        }
    }

    // Función para agregar una nueva promoción
    function addPromotion() {
        promotions = [...promotions, {
            type: "Recharge",
            ranges: [
                { min: 0, max: 19999, displayAmount: 0 }
            ]
        }];
    }

    // Función para eliminar una promoción
    function removePromotion(index: number) {
        promotions = promotions.filter((_, i) => i !== index);
    }

    // Función para agregar un nuevo rango a una promoción específica
    function addRange(promotionIndex: number) {
        if (!promotions[promotionIndex].ranges) {
            promotions[promotionIndex].ranges = [];
        }
        
        const newRange = { min: 0, max: 0, displayAmount: 0 };
        promotions[promotionIndex].ranges!.push(newRange);
        
        // Forzar la reactividad
        promotions = [...promotions];
    }

    // Función para eliminar un rango de una promoción específica
    function removeRange(promotionIndex: number, rangeIndex: number) {
        console.log("=== REMOVING RANGE ===");
        console.log("Removing range", rangeIndex, "from promotion", promotionIndex);
        console.log("Current promotions before removal:", JSON.stringify(promotions, null, 2));
        
        if (promotions[promotionIndex].ranges) {
            promotions[promotionIndex].ranges = promotions[promotionIndex].ranges!.filter((_, i) => i !== rangeIndex);
            promotions = [...promotions]; // Trigger reactivity
            
            console.log("Current promotions after removal:", JSON.stringify(promotions, null, 2));
            console.log("Generated JSON after removal:", generateSettingsJSON());
        }
        console.log("=== END REMOVING RANGE ===");
    }

    // Función para cambiar el tipo de promoción
    function changePromotionType(index: number, newType: string) {
        promotions[index].type = newType;
        if (newType === "Recharge") {
            promotions[index].ranges = [{ min: 0, max: 19999, displayAmount: 0 }];
            delete promotions[index].fixedAmount;
        } else {
            promotions[index].fixedAmount = 5000;
            delete promotions[index].ranges;
        }
        promotions = [...promotions]; // Trigger reactivity
    }





    // Hacer que editcampaigns se actualice cuando cambien los valores de tiempo
    $: {
        editcampaigns.Days = days;
        editcampaigns.Hours = hours;
        editcampaigns.Minutes = minutes;
        editcampaigns.Seconds = seconds;
        editcampaigns.TotalDays = convertToTimespan();
        
        const generatedSettings = generateSettingsJSON();
        editcampaigns.Settings = generatedSettings;
        console.log("Reactivity: Settings updated to:", generatedSettings);
        console.log("Current promotions state in reactivity:", JSON.stringify(promotions, null, 2));

        // Actualizar la fecha de expiración cuando cambie
        if (expirationDateValue) {
            try {
                editcampaigns.ExpirationDate = new Date(expirationDateValue);
            } catch {
                console.error("Invalid date format:", expirationDateValue);
            }
        }
    }
    

    
    // Actualizar TotalDays cuando cambien los valores de tiempo
    $: {
        editcampaigns.TotalDays = convertToTimespan();
    }

    async function EditCampaigns() {
        console.log("EditCampaigns function called");
        
        // Convertir el valor del input de dólares a centavos
        const startAmountValue = typeof startAmountInput === 'string' ? parseFloat(startAmountInput) || 0 : startAmountInput;
        const startAmountInCents = Math.round(startAmountValue * 100);
        
        const updatedEditCampaigns: EditCampaignsDTO = {
            ...editcampaigns,
            StartAmount: { InCents: startAmountInCents }
        };

        console.log("Settings being sent:", updatedEditCampaigns.Settings);
        console.log("Current promotions at send time:", JSON.stringify(promotions, null, 2));

        const editCampaignsRequest: AuthenticatedRequestOfEditCampaignsDTO = {
            Token: sessionStorage.token,
            Data: updatedEditCampaigns
        };

        try {
            console.log("Sending request to backend...");
            result = await service.walletapi.editCampaigns(editCampaignsRequest);
            
            if (result.Success) {
                console.log("Campaign updated successfully");
                dismiss();
            } else {
                console.error("Error updating Campaign:", result.ErrorMessage);
            }
        } catch (error) {
            console.error("Error updating campaign:", error);
        }
    }
</script>

<style>
    .edit-campaign-container {
        max-width: 350px;
        max-height: 85vh;
        margin: 0 auto;
        background: white;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .edit-campaign-container h2.ui.centered.dividing.header {
        font-size: 1.1rem;
        padding: 0.5rem;
        margin: 0;
        background: white;
        border-radius: 6px 6px 0 0;
        border-bottom: 1px solid #e9ecef;
        flex-shrink: 0;
        position: sticky;
        top: 0;
        z-index: 10;
        display: block !important;
        visibility: visible !important;
    }

    .edit-campaign-form {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0.5rem;
        max-height: calc(85vh - 80px);
        margin-top: 0;
    }

    .edit-campaign-form::-webkit-scrollbar {
        width: 8px;
    }

    .edit-campaign-form::-webkit-scrollbar-track {
        background: #f8f9fa;
        border-radius: 4px;
        border: 1px solid #e9ecef;
    }

    .edit-campaign-form::-webkit-scrollbar-thumb {
        background: #6c757d;
        border-radius: 4px;
        border: 1px solid #495057;
    }

    .edit-campaign-form::-webkit-scrollbar-thumb:hover {
        background: #495057;
    }

    .edit-campaign-container h2.ui.centered.dividing.header {
        font-size: 1.1rem;
        padding: 0.4rem 0;
        margin-bottom: 0.8rem;
    }

    .edit-campaign-form {
        margin-top: 0;
        padding-bottom: 0.5rem;
    }

    .form-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.3rem;
        margin-bottom: 0.3rem;
    }

    .field {
        margin-bottom: 0.4rem;
    }

    .field label {
        display: flex;
        align-items: center;
        gap: 0.2rem;
        font-weight: 600;
        color: #333;
        margin-bottom: 0.1rem;
        font-size: 0.8rem;
    }

    .field label i {
        color: #666;
        font-size: 0.75rem;
    }

    .field input {
        width: 100%;
        padding: 0.2rem 0.25rem;
        border: 1px solid #ddd;
        border-radius: 3px;
        font-size: 0.8rem;
        transition: all 0.2s ease;
        background: #fafbfc;
    }

    .field input:focus {
        outline: none;
        border-color: #2185d0;
        background: white;
        box-shadow: 0 0 0 2px rgba(33, 133, 208, 0.1);
    }

    .field select {
        width: 100%;
        padding: 0.2rem 0.25rem;
        border: 1px solid #ddd;
        border-radius: 3px;
        font-size: 0.8rem;
        transition: all 0.2s ease;
        background: #fafbfc;
    }

    .field select:focus {
        outline: none;
        border-color: #2185d0;
        background: white;
        box-shadow: 0 0 0 2px rgba(33, 133, 208, 0.1);
    }





    .advanced-settings {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 3px;
        padding: 0.2rem;
        margin-top: 0.2rem;
        font-size: 0.85rem;
    }

    .ranges-container {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    .range-row {
        display: flex;
        align-items: center;
        gap: 0.2rem;
        padding: 0.2rem;
        background: white;
        border: 1px solid #e9ecef;
        border-radius: 3px;
    }

    .range-inputs {
        display: flex;
        align-items: center;
        gap: 0.15rem;
        flex: 1;
    }

    .range-inputs input {
        width: 60px;
        font-size: 0.65rem;
        padding: 0.1rem 0.15rem;
        border: 1px solid #ddd;
        border-radius: 2px;
    }

    .range-separator {
        font-weight: bold;
        color: #666;
        font-size: 0.65rem;
    }



    .promotions-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.2rem;
    }

    .promotions-header label {
        display: flex;
        align-items: center;
        gap: 0.15rem;
        font-weight: 600;
        color: #333;
        font-size: 0.75rem;
    }

    .promotions-header label i {
        color: #666;
        font-size: 0.75rem;
    }

    .promotion-item {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 3px;
        padding: 0.2rem;
        margin-bottom: 0.2rem;
    }

    .promotion-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.2rem;
        padding-bottom: 0.2rem;
        border-bottom: 1px solid #e9ecef;
    }

    .promotion-number {
        font-weight: 600;
        color: #333;
        font-size: 0.75rem;
    }

    .time-inputs {
        display: flex;
        flex-direction: row;
        gap: 0.3rem;
        margin-bottom: 0.3rem;
    }

    .time-input-group {
        display: flex;
        flex-direction: column;
        gap: 0.1rem;
    }

    .time-input-group label {
        font-size: 0.65rem;
        font-weight: 500;
        color: #666;
    }

    .time-input-group input {
        width: 100%;
        font-size: 0.7rem;
        padding: 0.15rem 0.2rem;
        border: 1px solid #ddd;
        border-radius: 2px;
    }

    .time-preview {
        text-align: center;
        padding: 0.2rem;
        background: #f8f9fa;
        border-radius: 2px;
        border: 1px solid #e9ecef;
        margin-top: 0.1rem;
    }

    .time-preview small {
        color: #6c757d;
        font-style: italic;
        font-size: 0.65rem;
    }

    .form-actions {
        display: flex;
        gap: 0.3rem;
        justify-content: flex-end;
        margin-top: 0.4rem;
        padding-top: 0.4rem;
        border-top: 1px solid #e1e5e9;
    }

    .form-actions button {
        padding: 0.25rem 0.5rem;
        border-radius: 3px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 0.2rem;
        transition: all 0.2s ease;
        min-width: 70px;
        justify-content: center;
        font-size: 0.75rem;
    }

    .cancel-btn {
        background: #f8f9fa;
        color: #6c757d;
        border: 1px solid #e1e5e9;
    }

    .cancel-btn:hover {
        background: #e9ecef;
        color: #495057;
    }

    .save-btn {
        background: #21ba45;
        color: white;
        border: 1px solid #21ba45;
    }

    .save-btn:hover {
        background: #16ab39;
        border-color: #16ab39;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
        .edit-campaign-container {
            padding: 6px;
            margin: 6px;
            max-width: 95vw;
        }

        .form-grid {
            grid-template-columns: 1fr;
            gap: 4px;
        }

        .time-inputs {
            flex-direction: row;
        }

        .form-actions {
            flex-direction: column;
            gap: 8px;
        }

        .form-actions button {
            width: 100%;
        }
    }

    @media (max-width: 480px) {
        .edit-campaign-container {
            padding: 4px;
            margin: 4px;
        }

        .field input {
            padding: 6px 8px;
            font-size: 16px; /* Prevents zoom on iOS */
        }
    }
</style>

<div class="edit-campaign-container">
    <h2 class="ui centered dividing header">
        <i class="edit icon"></i>
        {$translate('Edit Campaign')}: {editcampaigns.PromotionName}
    </h2>

    {#if result && !result.Success}
        <div class="ui negative message">
            <div class="header">
                <i class="exclamation triangle icon"></i>
                Error
            </div>
            <p>{result.ErrorMessage ?? "Unknown error when updating campaign"}</p>
        </div>
    {/if}

    <form class="ui form edit-campaign-form" on:submit|preventDefault={EditCampaigns}>
        <div class="form-grid">
            <!-- Campo Nombre de la Promoción oculto -->
            <div class="field" style="display: none;">
                <label for="name">
                    <i class="fas fa-tag"></i>
                    Nombre de la Promoción
                </label>
                <input
                    type="text"
                    id="name"
                    bind:value={editcampaigns.PromotionName}
                    placeholder="Ingrese el nombre de la promoción"
                    required
                />
            </div>

            {#if editcampaigns.Type !== 'Recharge'}
                <div class="field">
                    <label for="amount">
                        <i class="fas fa-coins"></i>
                        Cantidad de Créditos
                    </label>
                    <input
                        type="number"
                        id="amount"
                        bind:value={startAmountInput}
                        placeholder="Ingrese la cantidad de créditos"
                        min="0"
                        required
                    />
                </div>
            {/if}

            <div class="field">
                <label for="isActive">
                    <i class="fas fa-toggle-on"></i>
                    Estado Activo
                </label>
                <input
                    type="checkbox"
                    id="isActive"
                    bind:checked={editcampaigns.Active}
                />
            </div>
        </div>

        <div class="field">
            <label for="totalDays">
                <i class="fas fa-clock"></i>
                Duración de la Promoción
            </label>
            <div class="time-inputs">
                <div class="time-input-group">
                    <label for="days">Días</label>
                    <input
                        type="number"
                        id="days"
                        bind:value={days}
                        placeholder="0"
                        min="0"
                        max="365"
                    />
                </div>
                <div class="time-input-group">
                    <label for="hours">Horas</label>
                    <input
                        type="number"
                        id="hours"
                        bind:value={hours}
                        placeholder="0"
                        min="0"
                        max="23"
                    />
                </div>
                <div class="time-input-group">
                    <label for="minutes">Minutos</label>
                    <input
                        type="number"
                        id="minutes"
                        bind:value={minutes}
                        placeholder="0"
                        min="0"
                        max="59"
                    />
                </div>
                <div class="time-input-group">
                    <label for="seconds">Segundos</label>
                    <input
                        type="number"
                        id="seconds"
                        bind:value={seconds}
                        placeholder="0"
                        min="0"
                        max="59"
                    />
                </div>
            </div>
            <!-- Campo Formato oculto -->
            <div class="time-preview" style="display: none;">
                <small>Formato: {editcampaigns.TotalDays}</small>
            </div>
        </div>

        <div class="field">
            <label for="expirationDate">
                <i class="fas fa-calendar-alt"></i>
                Fecha de Expiración
            </label>
            <input
                type="date"
                id="expirationDate"
                bind:value={expirationDateValue}
                required
            />
        </div>

        {#if editcampaigns.Type === 'Recharge'}
            <div class="field">
                <label for="settings">
                    <i class="fas fa-cog"></i>
                    Configuración de Promoción
                </label>
                
                <div class="advanced-settings">
                    <div class="promotions-header">
                            <label>
                                <i class="fas fa-layer-group"></i>
                                Configuración de Promociones
                            </label>
                            {#if promotions.length === 0}
                                <button type="button" class="ui small button" on:click={addPromotion}>
                                    <i class="plus icon"></i>
                                    Agregar Promoción
                                </button>
                            {/if}
                        </div>

                        {#each promotions as promotion, promotionIndex}
                            <div class="promotion-item">
                                <div class="promotion-header">
                                    <span class="promotion-number">Promoción {promotionIndex + 1}</span>
                                    <button type="button" class="ui small red icon button" on:click={() => removePromotion(promotionIndex)}>
                                        <i class="trash icon"></i>
                                    </button>
                                </div>

                                {#if promotion.type !== "Recharge"}
                                    <div class="field">
                                        <label for="promotionType{promotionIndex}">
                                            <i class="fas fa-list"></i>
                                            Tipo de Promoción
                                        </label>
                                        <select id="promotionType{promotionIndex}" bind:value={promotion.type} on:change={() => changePromotionType(promotionIndex, promotion.type)}>
                                            <option value="Recharge">Recharge (con rangos)</option>
                                            <option value="OtherPromoType">Otro tipo (valor fijo)</option>
                                        </select>
                                    </div>
                                {/if}

                                {#if promotion.type === "Recharge"}
                                    <div class="field">
                                        <label>
                                            <i class="fas fa-layer-group"></i>
                                            Rangos de Promoción
                                        </label>
                                        <div class="ranges-container">
                                            {#each promotion.ranges || [] as range, rangeIndex}
                                                <div class="range-row">
                                                    <div class="range-inputs">
                                                        <input
                                                            type="number"
                                                            placeholder="Mínimo"
                                                            bind:value={range.min}
                                                            min="0"
                                                        />
                                                        <span class="range-separator">-</span>
                                                        <input
                                                            type="number"
                                                            placeholder="Máximo"
                                                            bind:value={range.max}
                                                            min="0"
                                                        />
                                                        <span class="range-separator">→</span>
                                                        <input
                                                            type="number"
                                                            placeholder="Monto"
                                                            bind:value={range.displayAmount}
                                                            min="0"
                                                        />
                                                    </div>
                                                    <button type="button" class="ui small red icon button" on:click={() => removeRange(promotionIndex, rangeIndex)}>
                                                        <i class="trash icon"></i>
                                                    </button>
                                                </div>
                                            {/each}
                                            <button type="button" class="ui small button" on:click={() => addRange(promotionIndex)}>
                                                <i class="plus icon"></i>
                                                Agregar Rango
                                            </button>
                                        </div>
                                    </div>
                                {:else}
                                    <div class="field">
                                        <label for="fixedAmount{promotionIndex}">
                                            <i class="fas fa-dollar-sign"></i>
                                            Monto Fijo
                                        </label>
                                        <input
                                            type="number"
                                            id="fixedAmount{promotionIndex}"
                                            bind:value={promotion.fixedAmount}
                                            placeholder="Ingrese el monto fijo"
                                            min="0"
                                        />
                                    </div>
                                {/if}
                            </div>
                        {/each}
                    </div>
            </div>
        {/if}

        <!-- Campo Tipo de Promoción oculto -->
        <div class="field" style="display: none;">
            <label for="type">
                <i class="fas fa-list"></i>
                Tipo de Promoción
            </label>
            <select id="type" bind:value={editcampaigns.Type} required>
                <option value={TypePromotion.Register}>Registro</option>
                <option value={TypePromotion.Birthday}>Cumpleaños</option>
                <option value={TypePromotion.HappyFriday}>Viernes Feliz</option>
                <option value={TypePromotion.WelcomeBonus}>Bienvenida</option>
                <option value={TypePromotion.DailyBonus}>Bono Diario</option>
                <option value={TypePromotion.Manual}>Manual</option>
                <option value={TypePromotion.Recharge}>Recarga</option>
            </select>
        </div>

        <div class="form-grid">
            <!-- Campo Depósito Requerido oculto -->
            <div class="field" style="display: none;">
                <label for="depositRequired">
                    <i class="fas fa-money-bill-wave"></i>
                    Depósito Requerido
                </label>
                <input
                    type="checkbox"
                    id="depositRequired"
                    bind:checked={editcampaigns.DepositRequired}
                />
            </div>
        </div>

        <div class="form-actions">
            <button class="ui negative button cancel-btn" type="button" on:click={dismiss}>
                <i class="cancel icon"></i>
                {$translate('Cancel')}
            </button>
            <button class="ui positive button save-btn" type="submit">
                <i class="save icon"></i>
                {$translate('Update Campaign')}
            </button>
        </div>
    </form>
</div>